/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useLanguage } from "../../../contexts/LanguageContext";
import { initialsFrom } from "../../../utils/initials";
import { utcIsoToZonedWallTime } from "../../../utils/zonedTime";
import { stripHtml } from "../messageUtils";

const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

// Day and clock for the lock screen: the scheduled wall time when there is
// one, else the current time on the store's clock.
function lockScreenTime(wallTime, timeZone, language) {
  const match =
    WALL_TIME.exec(wallTime || "") ||
    WALL_TIME.exec(utcIsoToZonedWallTime(new Date().toISOString(), timeZone));
  if (!match) return { day: "", time: "" };
  const [, y, mo, d, h, mi] = match;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  let day = "";
  try {
    day = date.toLocaleDateString(language, {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    });
  } catch {
    day = "";
  }
  return { day, time: `${h}:${mi}` };
}

function Note({ children }) {
  return (
    <p className="m-0 max-w-[300px] text-center text-xs leading-normal text-gray-600">
      {children}
    </p>
  );
}

// What the customer sees, per channel. Email and SMS reach only customers in
// the audience who have the address and agreed to marketing messages (M5).
export default function MessagePreview({ formData, storeName, timeZone }) {
  const { t, language } = useLanguage();
  const channel = formData.message_type;
  const text = channel === "email" ? formData.message : formData.message.trim();
  const placeholder = t("marketing.preview.placeholder");

  if (channel === "email") {
    const hasBody = !!stripHtml(text);
    return (
      <>
        <div className="flex min-h-[440px] w-full max-w-[300px] flex-col overflow-hidden rounded-[14px] border border-gray-200 bg-white">
          <div className="flex flex-col gap-1 border-b border-gray-100 px-4 py-3.5">
            <span className="text-[15px] font-semibold text-gray-900 [overflow-wrap:anywhere]">
              {formData.subject || t("marketing.preview.subjectPlaceholder")}
            </span>
            <span className="text-xs text-gray-600 [overflow-wrap:anywhere]">
              {formData.from
                ? t("marketing.preview.fromLine", { from: formData.from })
                : t("marketing.preview.fromPlaceholder")}
            </span>
          </div>
          {hasBody ? (
            <div
              className="prose prose-sm max-w-none p-4 [overflow-wrap:anywhere]"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          ) : (
            <p className="m-0 p-4 text-sm text-gray-500">{placeholder}</p>
          )}
        </div>
        <Note>{t("marketing.preview.emailNote")}</Note>
      </>
    );
  }

  if (channel === "sms") {
    return (
      <>
        <div className="flex min-h-[440px] w-full max-w-[280px] flex-col gap-2.5 rounded-[32px] border border-gray-200 bg-white px-3.5 py-7">
          <span className="self-center text-xs text-gray-600">
            {formData.from || t("marketing.preview.noSender")}
          </span>
          <span className="max-w-[85%] self-start rounded-[18px_18px_18px_4px] bg-gray-100 px-3 py-2.5 text-sm leading-snug whitespace-pre-wrap text-gray-900 [overflow-wrap:anywhere]">
            {text || placeholder}
          </span>
        </div>
        <Note>{t("marketing.preview.smsNote")}</Note>
      </>
    );
  }

  const { day, time } = lockScreenTime(
    formData.when === "later" ? formData.scheduled_at : "",
    timeZone,
    language,
  );
  return (
    <>
      <div className="flex min-h-[440px] w-full max-w-[280px] flex-col items-center gap-7 rounded-[32px] bg-gray-800 px-3.5 pt-10 pb-7">
        <div className="flex flex-col items-center gap-0.5 text-white">
          <span className="text-[13px] text-gray-300">{day}</span>
          <span className="text-[54px] leading-none font-semibold tracking-tight tabular-nums">
            {time}
          </span>
        </div>
        <div className="flex w-full gap-2.5 rounded-2xl bg-white/90 p-3">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-xs font-bold text-white"
          >
            {initialsFrom(storeName) || "L"}
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex justify-between gap-2 text-xs">
              <span className="truncate font-semibold text-gray-900">
                {storeName}
              </span>
              <span className="text-gray-600">
                {t("marketing.preview.now")}
              </span>
            </span>
            <span className="text-[13px] leading-snug whitespace-pre-wrap text-gray-900 [overflow-wrap:anywhere]">
              {text || placeholder}
            </span>
          </span>
        </div>
      </div>
      <Note>{t("marketing.preview.pushNote")}</Note>
    </>
  );
}
